import React, {useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import {alpha, useTheme} from "@mui/material/styles";
import {AudioWaveformIcon} from "./ProjectThumbnail";

export interface ActivationSampleRecording {
    id: string;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    sourceUrl?: string;
    mediaType?: "audio" | "video";
    activationFlowId?: string;
    presetTranscriptId?: string;
    skipTranscription?: boolean;
    transcriptionDisabled?: boolean;
    activeTranscriptionId?: string;
}

export interface ActivationSampleProgressUpdate {
    title: string;
    detail?: string;
}

interface FirstExportActivationModalProps {
    open: boolean;
    showWelcome: boolean;
    samples: ActivationSampleRecording[];
    onClose: () => void;
    onTrySample: (
        sample: ActivationSampleRecording,
        opts?: { onProgress?: (update: ActivationSampleProgressUpdate) => void }
    ) => Promise<void> | void;
    onUploadRecordingFile: (
        file: File,
        opts?: { onProgress?: (update: ActivationSampleProgressUpdate) => void }
    ) => Promise<void> | void;
}

const FirstExportActivationModal = ({
    open,
    showWelcome,
    samples,
    onClose,
    onTrySample,
    onUploadRecordingFile,
}: FirstExportActivationModalProps) => {
    const theme = useTheme();
    const sampleThumbnailHeight = {xs: "168px", sm: "188px", md: "198px"};
    const primaryButtonBackground = theme.palette.mode === "dark" ? theme.palette.common.white : "#111827";
    const primaryButtonHoverBackground = theme.palette.mode === "dark" ? "#F3F4F6" : "#0B0B0B";
    const primaryButtonText = theme.palette.mode === "dark" ? "#111827" : "#F9FAFB";
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedSampleId, setSelectedSampleId] = useState<string>(samples[0]?.id ?? "");
    const [mode, setMode] = useState<"selection" | "processing">("selection");
    const [isStartingSample, setIsStartingSample] = useState(false);
    const [processingTitle, setProcessingTitle] = useState("Preparing sample");
    const [processingDetail, setProcessingDetail] = useState("Setting things up.");
    const [processingError, setProcessingError] = useState("");
    const [processingSample, setProcessingSample] = useState<ActivationSampleRecording | null>(null);
    const [processingFile, setProcessingFile] = useState<File | null>(null);
    const [processingFilePreviewUrl, setProcessingFilePreviewUrl] = useState("");

    useEffect(() => {
        if (samples.length === 0) {
            setSelectedSampleId("");
            return;
        }
        const hasCurrentSelection = samples.some((sample) => sample.id === selectedSampleId);
        if (!hasCurrentSelection) {
            setSelectedSampleId(samples[0].id);
        }
    }, [samples, selectedSampleId]);

    useEffect(() => {
        if (!open) {
            setMode("selection");
            setIsStartingSample(false);
            setProcessingTitle("Preparing sample");
            setProcessingDetail("Setting things up.");
            setProcessingError("");
            setProcessingSample(null);
            setProcessingFile(null);
            setProcessingFilePreviewUrl("");
        }
    }, [open]);

    useEffect(() => {
        if (!processingFile) {
            setProcessingFilePreviewUrl("");
            return;
        }

        if (!processingFile.type.startsWith("video/") && !processingFile.type.startsWith("image/")) {
            setProcessingFilePreviewUrl("");
            return;
        }

        const objectUrl = URL.createObjectURL(processingFile);
        setProcessingFilePreviewUrl(objectUrl);
        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [processingFile]);

    const selectedSample = useMemo(
        () => samples.find((sample) => sample.id === selectedSampleId) ?? null,
        [samples, selectedSampleId]
    );
    const modalHeadline = showWelcome
        ? "Export your first episode in a quick guided demo."
        : "See how Libretto turns recordings into finished episodes.";
    const modalSubtext = "Start a quick guided demo or upload your own recording.";

    const primaryButtonLabel = "Start the 60-second demo";
    const canClose = mode === "selection" || Boolean(processingError);

    const renderThumbnailMedia = ({
        sample,
        file,
    }: {
        sample?: ActivationSampleRecording | null;
        file?: File | null;
    }) => {
        if (file) {
            if (file.type.startsWith("video/") && processingFilePreviewUrl) {
                return (
                    <Box
                        component="video"
                        src={processingFilePreviewUrl}
                        muted
                        playsInline
                        autoPlay
                        loop
                        sx={{width: "100%", height: "100%", objectFit: "cover", display: "block"}}
                    />
                );
            }

            if (file.type.startsWith("image/") && processingFilePreviewUrl) {
                return (
                    <Box
                        component="img"
                        src={processingFilePreviewUrl}
                        alt={file.name}
                        sx={{width: "100%", height: "100%", objectFit: "cover", display: "block"}}
                    />
                );
            }

            if (file.type.startsWith("audio/")) {
                return (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            "& svg": {
                                width: "100%",
                                height: "100%",
                                display: "block",
                            },
                        }}
                    >
                        {AudioWaveformIcon("var(--app-bg-subtle)")}
                    </Box>
                );
            }

            return "Preview";
        }

        if (sample?.thumbnailUrl) {
            return (
                <Box
                    component="img"
                    src={sample.thumbnailUrl}
                    alt={sample.title}
                    sx={{width: "100%", height: "100%", objectFit: "cover", display: "block"}}
                />
            );
        }

        if (sample?.mediaType === "audio") {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        "& svg": {
                            width: "100%",
                            height: "100%",
                            display: "block",
                        },
                    }}
                >
                    {AudioWaveformIcon("var(--app-bg-subtle)")}
                </Box>
            );
        }

        return "Preview";
    };

    const renderSamplePreview = (sample: ActivationSampleRecording, isSelected: boolean) => {
        const isRecommended = String(sample?.activationFlowId || "").trim() === "first_export_transcript";

        return (
            <ButtonBase
            key={sample.id}
            onClick={mode === "processing" ? undefined : () => setSelectedSampleId(sample.id)}
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                flexDirection: "column",
                textAlign: "left",
                borderRadius: "14px",
                padding: "6px",
                border: "1px solid transparent",
                cursor: mode === "processing" ? "default" : "pointer",
                transition: "transform 140ms ease",
                "&:hover": mode === "processing" ? undefined : {
                    transform: "translateY(-1px)",
                },
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: sampleThumbnailHeight,
                    borderRadius: "12px",
                    overflow: "hidden",
                    position: "relative",
                    border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-canvas)",
                    boxShadow: isSelected
                        ? `0px 10px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                        : `0px 6px 18px ${alpha(theme.palette.common.black, 0.06)}`,
                    transition: "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "var(--app-text-subtle)",
                    }}
                >
                    {renderThumbnailMedia({sample})}
                </Box>
            </Box>
            <Box sx={{marginTop: "10px", paddingLeft: "2px", display: "flex", flexDirection: "column", gap: "6px"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap"}}>
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "var(--app-text-primary)",
                            lineHeight: "20px",
                        }}
                    >
                        {sample.title}
                    </Typography>
                    {isRecommended ? (
                        <Box
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "11px",
                                fontWeight: 600,
                                lineHeight: "16px",
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.14),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
                                borderRadius: "999px",
                                px: 1,
                                py: 0.2,
                            }}
                        >
                            Recommended
                        </Box>
                    ) : null}
                </Box>
                {sample.description && (
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "13px",
                            fontWeight: 400,
                            color: "var(--app-text-muted)",
                            lineHeight: "19px",
                        }}
                    >
                        {sample.description}
                    </Typography>
                )}
            </Box>
        </ButtonBase>
        );
    };

    const handleTrySampleClick = async () => {
        if (!selectedSample || isStartingSample) return;

        setMode("processing");
        setIsStartingSample(true);
        setProcessingError("");
        setProcessingTitle(`Starting with ${selectedSample.title}`);
        setProcessingDetail("Preparing your sample.");
        setProcessingSample(selectedSample);
        setProcessingFile(null);

        try {
            await Promise.resolve(
                onTrySample(selectedSample, {
                    onProgress: ({title, detail}) => {
                        if (title) setProcessingTitle(title);
                        if (typeof detail === "string") setProcessingDetail(detail);
                    },
                })
            );
        } catch (error) {
            const message =
                (error instanceof Error ? error.message : String(error || "")).trim() ||
                "Could not start with this sample. Please try again.";
            setProcessingError(message);
            setProcessingTitle("Could not start sample");
            setProcessingDetail("You can retry or upload your own recording.");
        } finally {
            setIsStartingSample(false);
        }
    };

    const handleChooseOwnRecordingClick = () => {
        if (mode === "processing" && !processingError) return;
        uploadInputRef.current?.click();
    };

    const handleUploadFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        event.target.value = "";
        if (!file || isStartingSample) return;

        if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
            setMode("processing");
            setIsStartingSample(false);
            setProcessingSample(null);
            setProcessingFile(file);
            setProcessingTitle("Unsupported file");
            setProcessingDetail("Upload an audio or video file.");
            setProcessingError("Only audio and video files are supported in this flow.");
            return;
        }

        setMode("processing");
        setIsStartingSample(true);
        setProcessingError("");
        setProcessingSample(null);
        setProcessingFile(file);
        setProcessingTitle(`Starting with ${file.name}`);
        setProcessingDetail("Preparing your recording.");

        try {
            await Promise.resolve(
                onUploadRecordingFile(file, {
                    onProgress: ({title, detail}) => {
                        if (title) setProcessingTitle(title);
                        if (typeof detail === "string") setProcessingDetail(detail);
                    },
                })
            );
        } catch (error) {
            const message =
                (error instanceof Error ? error.message : String(error || "")).trim() ||
                "Could not start with this file. Please try again.";
            setProcessingError(message);
            setProcessingTitle("Could not start recording");
            setProcessingDetail("You can retry with another file.");
        } finally {
            setIsStartingSample(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={canClose ? onClose : undefined}
            BackdropProps={{
                sx: {
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    backgroundColor: alpha(
                        theme.palette.common.black,
                        theme.palette.mode === "dark" ? 0.64 : 0.46
                    ),
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: "22px",
                    padding: 0,
                    width: "1020px",
                    maxWidth: "calc(100vw - 32px)",
                    minHeight: "560px",
                    position: "relative",
                    backgroundColor: "var(--app-bg-canvas)",
                    border: "1px solid var(--app-border-subtle)",
                    boxShadow: "0px 26px 70px rgba(15, 23, 42, 0.16)",
                },
            }}
        >
            <IconButton
                onClick={onClose}
                disabled={!canClose}
                sx={{position: "absolute", top: 16, right: 16, color: "var(--app-text-muted)"}}
            >
                <Box component="span" sx={{fontSize: "18px", lineHeight: 1}}>×</Box>
            </IconButton>
            <DialogContent sx={{padding: {xs: "28px 22px 28px", sm: "38px 38px 34px"}, display: "flex", flexDirection: "column"}}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        padding: {xs: "4px 2px", sm: "8px 4px"},
                    }}
                >
                    {showWelcome && !modalHeadline.startsWith("Welcome!") && (
                        <Typography
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "13px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: theme.palette.primary.main,
                                lineHeight: "18px",
                            }}
                        >
                            Welcome!
                        </Typography>
                    )}
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: {xs: "28px", sm: "36px"},
                            fontWeight: 700,
                            lineHeight: {xs: "36px", sm: "44px"},
                            letterSpacing: "-0.5px",
                            color: "var(--app-text-primary)",
                        }}
                    >
                        {modalHeadline}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "15px",
                            lineHeight: "22px",
                            color: "var(--app-text-muted)",
                        }}
                    >
                        {modalSubtext}
                    </Typography>
                </Box>

                <input
                    ref={uploadInputRef}
                    type="file"
                    onChange={handleUploadFileSelected}
                    style={{display: "none"}}
                    accept="audio/*,video/*"
                />

                {mode === "selection" && samples.length > 0 && (
                    <>
                        {/*<Typography*/}
                        {/*    sx={{*/}
                        {/*        marginTop: "20px",*/}
                        {/*        fontFamily: "Inter",*/}
                        {/*        fontSize: "14px",*/}
                        {/*        fontWeight: 600,*/}
                        {/*        color: "var(--app-text-muted)",*/}
                        {/*        lineHeight: "18px",*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    Choose a workflow to explore:*/}
                        {/*</Typography>*/}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))"},
                                gap: {xs: "12px", sm: "14px", lg: "16px"},
                                marginTop: "12px",
                            }}
                        >
                            {samples.map((sample) => renderSamplePreview(sample, sample.id === selectedSampleId))}
                        </Box>
                    </>
                )}

                {mode === "processing" && (processingSample || processingFile) && (
                    <Box
                        sx={{
                            marginTop: "24px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                gap: "16px",
                                alignItems: "stretch",
                                flexWrap: {xs: "wrap", sm: "nowrap"},
                            }}
                        >
                        <Box sx={{width: {xs: "100%", sm: "280px"}, flexShrink: 0, order: 1}}>
                            <Box
                                sx={{
                                    width: "100%",
                                    borderRadius: "14px",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: sampleThumbnailHeight,
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        backgroundColor: "var(--app-bg-canvas)",
                                        boxShadow: `0px 10px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontFamily: "Inter",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            color: "var(--app-text-subtle)",
                                        }}
                                    >
                                        {renderThumbnailMedia({sample: processingSample, file: processingFile})}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                minWidth: "260px",
                                order: 2,
                                minHeight: sampleThumbnailHeight,
                                height: sampleThumbnailHeight,
                                borderRadius: "14px",
                                border: processingError
                                    ? `1px solid ${alpha(theme.palette.error.main, 0.35)}`
                                    : `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                background: processingError
                                    ? alpha(theme.palette.error.main, 0.06)
                                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, var(--app-bg-canvas) 80%)`,
                                padding: "18px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: "10px",
                            }}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                {isStartingSample ? (
                                    <CircularProgress size={16} thickness={6} sx={{color: theme.palette.primary.main}}/>
                                ) : null}
                                <Typography
                                    sx={{
                                        fontFamily: "Inter",
                                        fontSize: "17px",
                                        fontWeight: 600,
                                        color: "var(--app-text-primary)",
                                        lineHeight: "22px",
                                    }}
                                >
                                    {processingTitle}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: "Inter",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "var(--app-text-muted)",
                                    lineHeight: "20px",
                                }}
                            >
                                {processingDetail}
                            </Typography>
                            {processingError ? (
                                <Typography
                                    sx={{
                                        fontFamily: "Inter",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: theme.palette.error.main,
                                        lineHeight: "18px",
                                    }}
                                >
                                    {processingError}
                                </Typography>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: "Inter",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        color: "var(--app-text-subtle)",
                                        lineHeight: "16px",
                                    }}
                                >
                                    We&apos;ll open the editor automatically when transcription is ready.
                                </Typography>
                            )}
                        </Box>
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "var(--app-text-primary)",
                                lineHeight: "20px",
                            }}
                        >
                            {processingSample?.title || processingFile?.name || "Selected recording"}
                        </Typography>
                    </Box>
                )}

                {mode === "selection" ? (
                    <Box sx={{display: "flex", flexDirection: "column", gap: "8px", marginTop: "22px"}}>
                        <Box sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, gap: "12px"}}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleTrySampleClick}
                                disabled={!selectedSample}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    fontFamily: "Inter",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    lineHeight: "22px",
                                    letterSpacing: "0.12px",
                                    backgroundColor: primaryButtonBackground,
                                    color: primaryButtonText,
                                    boxShadow: "none",
                                    minHeight: "50px",
                                    height: "50px",
                                    padding: "0 22px",
                                    flex: 1,
                                    "&:hover": {
                                        backgroundColor: primaryButtonHoverBackground,
                                        boxShadow: "none",
                                    },
                                    "&.Mui-disabled": {
                                        backgroundColor: "var(--app-bg-subtle)",
                                        color: "var(--app-text-subtle)",
                                    },
                                }}
                            >
                                {primaryButtonLabel}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleChooseOwnRecordingClick}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    fontFamily: "Inter",
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    letterSpacing: "0.12px",
                                    minHeight: "50px",
                                    height: "50px",
                                    padding: "0 18px",
                                    flex: 1,
                                    color: "var(--app-text-primary)",
                                    backgroundColor: "var(--app-bg-surface)",
                                    borderColor: "var(--app-border-subtle)",
                                    "&:hover": {
                                        borderColor: "var(--app-border-muted)",
                                        backgroundColor: "var(--app-bg-canvas)",
                                    },
                                }}
                            >
                                Upload your own recording
                            </Button>
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "var(--app-text-subtle)",
                                lineHeight: "16px",
                                alignSelf: "center",
                            }}
                        >
                            No credit card required.
                        </Typography>
                    </Box>
                ) : (
                    processingError ? (
                        <Box sx={{display: "flex", flexDirection: "row", gap: "12px", marginTop: "20px"}}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={processingSample ? handleTrySampleClick : handleChooseOwnRecordingClick}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    fontFamily: "Inter",
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    backgroundColor: primaryButtonBackground,
                                    color: primaryButtonText,
                                    boxShadow: "none",
                                    minHeight: "50px",
                                    padding: "0 18px",
                                    "&:hover": {
                                        backgroundColor: primaryButtonHoverBackground,
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                {processingSample ? "Try again" : "Choose another file"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleChooseOwnRecordingClick}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    fontFamily: "Inter",
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    minHeight: "50px",
                                    padding: "0 18px",
                                }}
                            >
                                Upload your own recording
                            </Button>
                        </Box>
                    ) : null
                )}

                {mode === "selection" ? (
                    <>
                        {/*<Typography*/}
                        {/*    sx={{*/}
                        {/*        marginTop: "8px",*/}
                        {/*        alignSelf: "center",*/}
                        {/*        fontFamily: "Inter",*/}
                        {/*        fontSize: "12px",*/}
                        {/*        fontWeight: 500,*/}
                        {/*        color: "var(--app-text-subtle)",*/}
                        {/*        lineHeight: "16px",*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    Takes less than a minute.*/}
                        {/*</Typography>*/}
                        <Button
                            variant="text"
                            onClick={onClose}
                            sx={{
                                textTransform: "none",
                                fontFamily: "Inter",
                                fontWeight: 500,
                                fontSize: "13px",
                                lineHeight: "18px",
                                color: "var(--app-text-muted)",
                                alignSelf: "center",
                                minWidth: "auto",
                                padding: "2px 6px",
                                marginTop: "14px",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            Explore the editor instead
                        </Button>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default FirstExportActivationModal;
